import { useQuery, gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { UserProfileFragment } from '../fragments/UserProfile';
import { PageSEOFragment } from '../fragments/pages/PageSEOFragment';
import {
  Header,
  Footer,
  Main,
  Container,
  NavigationMenu,
  SEO,
  SchemaMarkup,
} from '../components';
import EntryHeaderFaq from '../components/EntryHeaderFaq/EntryHeaderFaq';
import React, { useEffect, useState, useMemo } from 'react';
import TopHero from '../components/skeletonloader/TopHero';
import classNames from 'classnames/bind';
import styles from '../styles/pages/OfteSporsmol.module.scss';
import { RiCloseLine, RiArrowDownLine } from 'react-icons/ri';
import ContactCTA from '../components/Global/ContactCTA/ContactCTA';
import { createFAQPageSchema, createBreadcrumbSchema, createSchema } from '../lib/schema';

let cx = classNames.bind(styles);

const QuestionModal = ({ isOpen, onClose, question }) => {
  if (!isOpen || !question) return null;

  return (
    <div className={cx('modalOverlay')} onClick={onClose}>
      <div className={cx('modalContent')} onClick={(e) => e.stopPropagation()}>
        <button className={cx('closeButton')} onClick={onClose}>
          <RiCloseLine />
        </button>
        
        <div className={cx('modalHeader')}>
          <h2 className={cx('modalTitle')}>{question.question}</h2>
        </div>
        
        <div className={cx('modalBody')}>
          {question.videoLink && (
            <div className={cx('videoContainer')}>
              <iframe
                src={question.videoLink.replace('youtu.be/', 'youtube.com/embed/')}
                title={question.question}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <div 
            className={cx('answer')}
            dangerouslySetInnerHTML={{ __html: question.answer }}
          />
        </div>
      </div>
    </div>
  );
};

const ServiceAccordion = ({ service, questions }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  return (
    <div className={cx('accordion')}>
      <button 
        className={cx('accordionHeader', { expanded: isExpanded })}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>{service.title}</h3>
        <RiArrowDownLine className={cx('arrow', { expanded: isExpanded })} />
      </button>
      
      {isExpanded && questions && (
        <div className={cx('accordionContent')}>
          {questions.map((question, index) => (
            <button
              key={index}
              className={cx('questionButton')}
              onClick={() => setSelectedQuestion(question)}
            >
              {question.question}
            </button>
          ))}
        </div>
      )}
      
      <QuestionModal
        isOpen={!!selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
        question={selectedQuestion}
      />
    </div>
  );
};

export default function OfteSporsmol(props) {
  const [mounted, setMounted] = useState(false);
  const { data, loading = false, error = null } = { data: props.data || {} };

  useEffect(() => {
    setMounted(true);
  }, []);

  if (error) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const viewer = data?.viewer;
  const page = data?.page;

  const heroSection = page?.faq?.layout?.find(
    section => section.__typename === 'FaqLayoutEntryHeaderLayout'
  );

  const faqSection = page?.faq?.layout?.find(
    section => section.__typename === 'FaqLayoutFaqSystemLayout'
  );

  const isLoading = loading || !mounted;

  // Get the page description from the hero section or fall back to site description
  const pageDescription = heroSection?.longDescription || siteDescription;
  
  // SEO values with comprehensive social media metadata
  const pageTitle = page?.seo?.title || 'Ofte stilte spørsmål';
  const siteName = siteTitle || 'Mediabooster';
  const finalTitle = pageTitle?.includes(siteName) ? pageTitle : `${pageTitle} - ${siteName}`;
  const finalDescription = page?.seo?.description || pageDescription || 'Finn svar på ofte stilte spørsmål om våre tjenester innenfor digital markedsføring, webdesign og teknologi.';
  
  // Get social image from SEO settings or use fallback
  const socialImage = page?.seo?.openGraph?.image?.url || page?.seo?.openGraph?.image?.secureUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
  const socialImageAlt = page?.seo?.openGraph?.image?.altText || 'Mediabooster - Ofte stilte spørsmål';

  // Extract all FAQ questions from service categories for schema markup
  const allFaqs = useMemo(() => {
    if (!faqSection?.serviceCategory) return [];
    return faqSection.serviceCategory.flatMap(category => 
      (category.question || []).map(q => ({
        question: q.question,
        answer: q.answer
      }))
    );
  }, [faqSection]);

  // Generate FAQ schema for structured data
  const faqSchema = useMemo(() => {
    const breadcrumbs = [
      { label: 'Hjem', path: '/' },
      { label: 'Ofte stilte spørsmål', path: '/ofte-stilte-sporsmal' }
    ];

    return createFAQPageSchema({
      title: finalTitle,
      description: finalDescription,
      url: '/ofte-stilte-sporsmal',
      faqs: allFaqs,
      breadcrumbs,
      includeBase: false
    });
  }, [finalTitle, finalDescription, allFaqs]);

  return (
    <>
      <SEO 
        seo={{
          title: finalTitle,
          description: finalDescription,
          canonicalUrl: 'https://mediabooster.no/ofte-stilte-sporsmal/',
          openGraph: {
            title: finalTitle,
            description: finalDescription,
            url: 'https://mediabooster.no/ofte-stilte-sporsmal/',
            siteName: siteTitle || 'Mediabooster',
            type: 'website',
            image: {
              url: socialImage,
              secureUrl: socialImage,
              width: 1200,
              height: 630,
              alt: socialImageAlt
            }
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: finalTitle,
            description: finalDescription,
            image: socialImage
          }
        }}
      />
      <SchemaMarkup schema={faqSchema} />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        viewer={viewer}
        sharedData={props.sharedData}
      />
      <Main style={{ paddingTop: 'var(--header-height, 80px)' }}>
        {isLoading ? (
          <TopHero />
        ) : (
          <>
            {heroSection && (
              <EntryHeaderFaq
                title={heroSection.header}
                longDescription={heroSection.longDescription}
                smallHeader="Ofte stilte spørsmål"
              />
            )}
            <Container>
              <div className={cx('faqContainer')}>
                {faqSection?.serviceCategory?.map((category, index) => (
                  <ServiceAccordion
                    key={`${category.selectService.nodes[0].id}-${index}`}
                    service={category.selectService.nodes[0]}
                    questions={category.question}
                  />
                ))}
              </div>
              <div className={cx('contactCtaWrapper')}>
                <ContactCTA sharedData={props.sharedData} />
              </div>
            </Container>
          </>
        )}
      </Main>
      <Footer 
        title={siteTitle} 
        menuItems={primaryMenu}
        settings={data}
        sharedData={props.sharedData}
      />
    </>
  );
}

OfteSporsmol.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${UserProfileFragment}
  ${PageSEOFragment}
  query GetFaqPage(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    ...UserProfileFragment
    page(id: "/ofte-stilte-sporsmal", idType: URI) {
      ...PageSEOFragment
      faq {
        layout {
          __typename
          ... on FaqLayoutEntryHeaderLayout {
            longDescription
            header
          }
          ... on FaqLayoutFaqSystemLayout {
            serviceCategory {
              selectService {
                nodes {
                  ... on Tjeneste {
                    id
                    title
                  }
                }
              }
              question {
                question
                videoLink
                answer
              }
            }
          }
        }
      }
    }
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

OfteSporsmol.variables = () => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
  };
};

// Add getStaticProps for server-side data fetching with shared data
export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const { ContactCTAFragment } = await import('../fragments/MeetingBookingCta');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: OfteSporsmol.query,
      variables: OfteSporsmol.variables(),
      fetchPolicy: 'no-cache'
    });
    
    // Fetch shared data using exact same queries as components
    const { data: sharedData } = await apolloClient.query({
      query: gql`
        ${ContactCTAFragment}
        query GetSharedFaqData {
          # Footer data - exact same query as Footer component
          tjenester(first: 100, where: { taxQuery: { taxArray: { terms: "4", field: TAXONOMY_ID } } }) {
            nodes {
              id
              title
              slug
              uri
              terms {
                nodes {
                  id
                  name
                }
              }
              tjenesterPost {
                serviceDescription
              }
            }
          }
          
          # MediaBooster settings - includes both Footer and ContactCTA data
          mediaboosterSettings {
            mediaboosterSettingsFields {
              logo {
                node {
                  mediaItemUrl
                  altText
                }
              }
              logoIcon {
                node {
                  mediaItemUrl
                  altText
                }
              }
              nyhetsbrev {
                tittelNyhetsbrev
                innholdNyhetsbrev
              }
            }
            ...ContactCTAFragment
          }
          
          # Frontpage tjenesteboks data used by Header
          page(id: "14", idType: DATABASE_ID) {
            frontPage {
              tjenesteboks {
                tjenestenavn
                underoverskrift
                link
              }
            }
          }
        }
      `,
      fetchPolicy: 'no-cache'
    });
    
    return {
      props: {
        data: pageData,
        sharedData: sharedData
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching FAQ data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}